import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { spinner, errorState, emptyState } from "../components/ui.js";
import { stars } from "../components/StarRating.js";

const SORTS = [["popular", "Popular"], ["top", "Top rated"], ["new", "Newest"]];
const PROGRAM_SORTS = [["popular", "Popular"], ["new", "Newest"]];

export default function Discover() {
  const root = h("div", { class: "screen" });
  let kind = "workouts"; // "workouts" | "programs"
  let sort = "popular";
  let q = "";
  let timer = null;

  load();

  async function load() {
    setChildren(root, header(), kindTabs(), controls(), spinner());
    try {
      if (kind === "programs") {
        const list = await api.discoverPrograms(`?sort=${sort}&q=${encodeURIComponent(q)}`);
        renderList(list, programCard, { icon: "list", title: "No programs yet", message: q ? "No programs match your search." : "Publish a program from your Programs list to share it." });
      } else {
        const list = await api.discoverWorkouts(`?sort=${sort}&q=${encodeURIComponent(q)}`);
        renderList(list, workoutCard, { icon: "search", title: "Nothing here yet", message: q ? "No workouts match your search." : "Be the first to publish a workout from your Workouts list." });
      }
    } catch (e) {
      setChildren(root, header(), kindTabs(), controls(), errorState(e.message, load));
    }
  }

  function header() {
    return topbar("Discover", { sub: "Shared by the community" });
  }

  function kindTabs() {
    return h("div", { class: "tabs", style: { marginBottom: "10px" } },
      ...[["workouts", "Workouts"], ["programs", "Programs"]].map(([key, label]) =>
        h("button", { class: kind === key && "active", onClick: () => { if (kind === key) return; kind = key; sort = "popular"; load(); } }, label)
      )
    );
  }

  function controls() {
    const search = h("input", {
      class: "input", type: "search", placeholder: kind === "programs" ? "Search programs…" : "Search workouts…", value: q,
      oninput: (e) => { q = e.target.value; clearTimeout(timer); timer = setTimeout(load, 300); },
    });
    const sorts = kind === "programs" ? PROGRAM_SORTS : SORTS;
    return h("div", null,
      h("div", { class: "field" }, search),
      h("div", { class: "tabs" }, ...sorts.map(([key, label]) =>
        h("button", { class: sort === key && "active", onClick: () => { sort = key; load(); } }, label)
      ))
    );
  }

  function renderList(list, card, empty) {
    setChildren(root, header(), kindTabs(), controls(),
      list.length
        ? h("div", { class: "mt" }, ...list.map(card))
        : emptyState(empty)
    );
  }

  function workoutCard(w) {
    const mini = MuscleMap({ primary: w.primary_muscle_ids || [] });
    mini.classList.add("mini");
    mini.style.maxWidth = "92px";
    const author = w.owner_display_name || w.owner_username;
    return h("div", { class: "card card-tap", style: { marginBottom: "12px" }, onClick: () => navigate(`/discover/${w.id}`) },
      h("div", { class: "hstack", style: { alignItems: "flex-start" } },
        mini,
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, w.name),
          h("div", { class: "dim", style: { marginTop: "2px" } }, "by ", author, w.source_author ? ` · adapted from ${w.source_name ? `${w.source_name} by ${w.source_author}` : w.source_author}` : ""),
          w.description ? h("div", { class: "dim", style: { marginTop: "4px", fontSize: "0.82rem" } }, w.description.slice(0, 100)) : null,
          h("div", { class: "hstack", style: { marginTop: "8px", gap: "12px", flexWrap: "wrap", fontSize: "0.82rem" } },
            w.rating_count ? h("span", { class: "hstack", style: { gap: "4px" } }, stars(w.avg_rating, { size: ".8rem" }), h("span", { class: "dim" }, `${w.avg_rating} (${w.rating_count})`)) : h("span", { class: "dim" }, "No ratings yet"),
            h("span", { class: "dim hstack", style: { gap: "4px" } }, icon("user", { size: 14 }), `${w.use_count} using`),
            h("span", { class: "dim" }, `${w.exercise_count} exercises`)
          )
        )
      )
    );
  }

  function programCard(p) {
    const weeks = Math.ceil(p.day_count / 7) || 0;
    const author = p.owner_display_name || p.owner_username;
    return h("div", { class: "card card-tap", style: { marginBottom: "12px" }, onClick: () => navigate(`/discover/program/${p.id}`) },
      h("div", { class: "hstack", style: { alignItems: "flex-start", gap: "12px" } },
        h("div", { class: "center", style: { flex: "0 0 auto", width: "52px", height: "52px", borderRadius: "12px", background: "var(--bg)", color: "var(--accent)" } }, icon("list", { size: 26 })),
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, p.name),
          h("div", { class: "dim", style: { marginTop: "2px" } }, "by ", author, p.source_author ? ` · adapted from ${p.source_name ? `${p.source_name} by ${p.source_author}` : p.source_author}` : ""),
          p.description ? h("div", { class: "dim", style: { marginTop: "4px", fontSize: "0.82rem" } }, p.description.slice(0, 100)) : null,
          h("div", { class: "hstack", style: { marginTop: "8px", gap: "12px", flexWrap: "wrap", fontSize: "0.82rem" } },
            h("span", { class: "dim" }, `${p.day_count} day${p.day_count === 1 ? "" : "s"}${weeks > 1 ? ` · ${weeks} weeks` : ""}`),
            h("span", { class: "dim" }, `${p.workout_day_count} workout${p.workout_day_count === 1 ? "" : "s"}`),
            h("span", { class: "dim hstack", style: { gap: "4px" } }, icon("user", { size: 14 }), `${p.use_count} enrolled`)
          )
        )
      )
    );
  }

  return root;
}
